// @ts-check
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import DataRow from './shared/DataRow';
import { DASHBOARD_COLORS } from './styles';
import { useEmergencyProvider } from '@/context_providers/emergency_context/EmergencyProvider';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import { obsidian } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useEffect, useState } from 'react';
import { UserInfo } from '../UserDashboard';
import { UserHistoryOnAzure } from '../../../../../../generated-client/models';

SyntaxHighlighter.registerLanguage('json', json);

interface DeveloperTabProps {
  value: number;
  index: number;
  userInfo: UserInfo;
}

const DeveloperTab = ({ value, index, userInfo }: DeveloperTabProps) => {
  const [emergencyHistory, setEmergencyHistory] =
    useState<UserHistoryOnAzure | null>();
  const [isLoadingEmergencyHistory, setIsLoadingEmergencyHistory] =
    useState(false);

  const { getEmergenciesByUser } = useEmergencyProvider();

  useEffect(() => {
    const fetchUserEmergencies = async () => {
      try {
        setIsLoadingEmergencyHistory(true);
        if (!userInfo.userAuthId || emergencyHistory === null) {
          console.log('No userAuthId or emergency history already loaded');
          return;
        }
        const emergencies = await getEmergenciesByUser(userInfo.userAuthId);
        setEmergencyHistory(emergencies);
        setIsLoadingEmergencyHistory(false);
      } catch (error) {
        setEmergencyHistory(null);
        setIsLoadingEmergencyHistory(false);
        console.error('Error fetching user emergencies:', error);
      }
    };

    fetchUserEmergencies();
  }, [userInfo.userAuthId, getEmergenciesByUser]);

  if (value !== index) {
    return null;
  }

  return (
    <Box sx={{ p: 1 }}>
      {/* Main Content - Row-based Layout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 2, md: 1 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              lg: 'row',
            },
            gap: 2,
          }}
        >
          <Paper
            //@ts-ignore
            variant="contained"
            sx={{
              p: 2,
              flex: {
                xs: 1,
                md: 3,
              },
              backgroundColor: DASHBOARD_COLORS.background,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body1"
              fontSize=".95rem"
              sx={{ fontWeight: 'bold', mr: 1 }}
            >
              {'Additional User Data'}
            </Typography>
            <DataRow label={'Auth ID'}>
              <Typography variant="body1" sx={{ fontSize: '.95rem' }}>
                {userInfo.userAuthId}
              </Typography>
            </DataRow>
            <DataRow label={'User Language Code'}>
              <Typography variant="body1" sx={{ fontSize: '.95rem' }}>
                {userInfo.languageCode || 'N/A'}
              </Typography>
            </DataRow>
          </Paper>
          <Paper
            // @ts-ignore
            variant="contained"
            sx={{
              p: 2,
              flex: {
                xs: 1,
                md: 3,
              },
              backgroundColor: DASHBOARD_COLORS.background,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body1"
              fontSize=".95rem"
              sx={{ fontWeight: 'bold', mr: 1 }}
            >
              {'Previous Emergencies'}
            </Typography>
            {isLoadingEmergencyHistory ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={'400px'}
                sx={{ borderRadius: '5px' }}
              />
            ) : (
              <SyntaxHighlighter
                language="json"
                style={obsidian}
                customStyle={{
                  fontSize: '0.75rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  borderRadius: '5px',
                }}
              >
                {JSON.stringify(
                  emergencyHistory?.observed_emergencies,
                  null,
                  2,
                )}
              </SyntaxHighlighter>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DeveloperTab;
